import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Divider, Skeleton } from "@mui/material";
import { auth } from "../../config/firebaseConfig";
import { getApiUrl, getApiHeaders } from "../../config/api";

const NotificationOverlay = ({ onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const markAsRead = async (notificationId) => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const token = await user.getIdToken();
      await fetch(getApiUrl(`api/notifications/${notificationId}/read`), {
        method: "POST",
        headers: getApiHeaders({ Authorization: `Bearer ${token}` }),
      });
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        const res = await fetch(getApiUrl("api/notifications?unread_only=false&limit=5"), {
          headers: getApiHeaders({ Authorization: `Bearer ${token}` })
        });
        if (!res.ok) return;
        const data = await res.json();
        setNotifications(data.notifications || []);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "#e1ecc8",
        borderRadius: "0 0 20px 20px",
        maxWidth: "90vw",
        width: { xs: "280px", sm: "300px", md: "321px" },
        padding: { xs: "15px", sm: "20px" },
        paddingTop: "30px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", color: "#516d4f" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", fontFamily: "Poppins, sans-serif" }}>Notifications</Typography>
      </Box>

      {/* Notification Items */}
      <Box sx={{ maxHeight: "256px", overflowY: "auto", paddingRight: "5px" }}>
        {loading ? (
          // Skeleton loading state
          <>
            {[1, 2, 3].map((item) => (
              <Box key={item} sx={{ padding: "12px", marginBottom: "8px" }}>
                <Skeleton 
                  variant="text" 
                  width="60%" 
                  height={24} 
                  sx={{ bgcolor: 'rgba(81, 109, 79, 0.15)', marginBottom: "8px" }} 
                />
                <Skeleton 
                  variant="text" 
                  width="90%" 
                  height={16} 
                  sx={{ bgcolor: 'rgba(81, 109, 79, 0.15)', marginBottom: "4px" }} 
                />
                <Skeleton 
                  variant="text" 
                  width="75%" 
                  height={16} 
                  sx={{ bgcolor: 'rgba(81, 109, 79, 0.15)' }} 
                />
                <Divider sx={{ my: 1 }} />
              </Box>
            ))}
          </>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <Box 
              key={notification.id} 
              sx={{ 
                padding: "12px", 
                borderRadius: "12px", 
                transition: "background 0.3s", 
                cursor: "pointer",
                backgroundColor: notification.is_read ? 'transparent' : 'rgba(160, 196, 157, 0.15)',
                "&:hover": { backgroundColor: "#d1dfb5" } 
              }}
              onClick={() => {
                // Mark as read before navigating
                if (!notification.is_read) {
                  markAsRead(notification.id);
                }
                navigate('/user/notification', { state: { highlightId: notification.id } });
                if (onClose) onClose();
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: notification.is_read ? 600 : 700, 
                  fontFamily: "Poppins, sans-serif", 
                  color: notification.is_read ? "#707070" : "#516d4f" 
                }}
              >
                {notification.title}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: notification.is_read ? "#707070" : "#516d4f",
                  fontWeight: notification.is_read ? 400 : 500
                }}
              >
                {notification.message}
              </Typography>
              <Divider sx={{ my: 1 }} />
            </Box>
          ))
        ) : (
          <Typography variant="body2" sx={{ textAlign: "center", color: "#999" }}>
            No new notifications.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default NotificationOverlay;
